import React, { useContext, useEffect, useMemo, useState } from 'react'

import LoadingContext from '../../context/Loading/Loading'
import UserContext from '../../context/User/User'

import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'
import {
  addWorksheet,
  capitalizeString,
  checkPermission,
  createWorkbook,
  formatNumber,
  saveWorkbook,
  setColumnsAndRows,
  setHeaders,
} from '../../utilities/helpers'

import AuthService from '../../utilities/services/auth.service'

import { getMarketingOptIns, removeMarketingOptIn } from '../../utilities/api'

import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'
import Stack from 'react-bootstrap/Stack'

import { DeleteModal } from '../DeleteModal'
import { EmptyContainer } from '../EmptyContainer'
import { NoPermissionsContainer } from '../NoPermissionsContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'

import './promoEmailsWrapper.scss'

const canSendPromoEmails = (orgPermissionsReady, getPermissions, user) => {
  if (user?.user?.superAdmin) return true
  return (
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.CONTACT_ATTENDEES
    )
  )
}

const contactPurchases = (contact) => {
  if (contact?.purchaseNames?.length) return contact.purchaseNames
  if (contact?.purchaseName) {
    return contact.purchaseName.split(', ').filter(Boolean)
  }
  return []
}

const formatContact = (contact, purchaseFilter = '') => ({
  first_name: capitalizeString(contact.firstName || '') || '',
  last_name: capitalizeString(contact.lastName || '') || '',
  email: contact.email || '',
  purchase: purchaseFilter || contact.purchaseName || '',
})

export default function PromoEmailsWrapper() {
  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)
  const { orgPermissions } = useContext(UserContext)
  const { getPermissions, getOrg, getUser } = AuthService

  const orgPermissionsReady = orgPermissions?.length > 0
  const user = getUser()
  const organization = getOrg()?.[0]
  const isSuperUser = Boolean(
    user?.user?.uuid &&
    organization?.members?.some(
      (member) => member?.uuid === user.user.uuid && member?.superAdmin
    )
  )
  const hasPermission =
    isSuperUser || canSendPromoEmails(orgPermissionsReady, getPermissions, user)

  const [contacts, setContacts] = useState([])
  const [purchaseFilter, setPurchaseFilter] = useState('')
  const [loadError, setLoadError] = useState('')
  const [contactToRemove, setContactToRemove] = useState(null)
  const [isRemoving, setIsRemoving] = useState(false)

  const organizationName = organization?.name || 'your organization'

  const loadContacts = () => {
    if (!organization?.uuid) return Promise.resolve()
    return getMarketingOptIns(organization.uuid).then((optIns) => {
      setContacts(optIns?.data?.contacts || [])
    })
  }

  useEffect(() => {
    if (!orgPermissionsReady) return
    if (!hasPermission || !organization?.uuid) {
      hideLoading()
      return
    }

    let cancelled = false
    showLoading()
    loadContacts()
      .catch((error) => {
        console.error(error)
        if (!cancelled) {
          setLoadError(
            error?.response?.data?.error?.message ||
              'Unable to load opted-in contacts.'
          )
        }
      })
      .finally(() => {
        if (!cancelled) hideLoading()
      })

    return () => {
      cancelled = true
    }
  }, [orgPermissionsReady, hasPermission, organization?.uuid])

  const purchaseOptions = useMemo(() => {
    const names = new Set()
    contacts.forEach((contact) => {
      contactPurchases(contact).forEach((name) => names.add(name))
    })
    return [...names].sort((a, b) => a.localeCompare(b))
  }, [contacts])

  const filteredContacts = useMemo(() => {
    if (!purchaseFilter) return contacts
    return contacts.filter((contact) =>
      contactPurchases(contact).includes(purchaseFilter)
    )
  }, [contacts, purchaseFilter])

  useEffect(() => {
    if (purchaseFilter && !purchaseOptions.includes(purchaseFilter)) {
      setPurchaseFilter('')
    }
  }, [purchaseFilter, purchaseOptions])

  const handleExcelExport = () => {
    if (!filteredContacts.length) return

    const workbook = createWorkbook()
    const worksheet = addWorksheet('Opted-in emails', workbook)

    setHeaders('Opted-in emails', organization?.name, worksheet)
    setColumnsAndRows(
      Object.keys(formatContact(filteredContacts[0], purchaseFilter)),
      filteredContacts,
      (contact) => formatContact(contact, purchaseFilter),
      worksheet
    )

    saveWorkbook(workbook, 'opted-in emails')
      .then()
      .catch((err) => alert(err.message))
  }

  const handleRemove = () => {
    if (!organization?.uuid || !contactToRemove?.email) return
    setIsRemoving(true)
    setLoadError('')
    removeMarketingOptIn({
      organizationUUID: organization.uuid,
      email: contactToRemove.email,
      ...(purchaseFilter ? { purchaseName: purchaseFilter } : {}),
    })
      .then(() => loadContacts())
      .then(() => setContactToRemove(null))
      .catch((error) => {
        console.error(error)
        setLoadError(
          error?.response?.data?.error?.message ||
            'Unable to remove this contact.'
        )
      })
      .finally(() => setIsRemoving(false))
  }

  if (!hasPermission && !orgPermissionsReady) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  if (!hasPermission) {
    return (
      <div className="spacer-sm">
        <NoPermissionsContainer />
      </div>
    )
  }

  if (isLoading) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  const removeName = [
    capitalizeString(contactToRemove?.firstName || ''),
    capitalizeString(contactToRemove?.lastName || ''),
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <section
      className="spacer-sm promo-emails-wrapper"
      data-testid="promo-emails-wrapper-content"
    >
      <header className="section-heading section-header-sm">
        <h1>Promo emails</h1>
        <p className="section-header-desc">
          People who opted in at checkout to emails from {organizationName}.
        </p>
      </header>

      <Stack
        direction="horizontal"
        gap={3}
        className="mb-4 align-items-end flex-wrap"
      >
        <Form.Group controlId="promo-purchase-filter">
          <Form.Label>Purchase</Form.Label>
          <Form.Select
            value={purchaseFilter}
            onChange={(event) => setPurchaseFilter(event.target.value)}
            disabled={contacts.length === 0}
          >
            <option value="">All purchases</option>
            {purchaseOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Button
          variant="outline-light"
          className="icon-button btn-download ms-auto"
          disabled={filteredContacts.length === 0}
          onClick={handleExcelExport}
        >
          Export to Excel
        </Button>
      </Stack>

      {loadError && (
        <Alert variant="danger" className="mb-4">
          {loadError}
        </Alert>
      )}

      <Card body>
        {filteredContacts.length > 0 ? (
          <>
            <p className="mb-4">
              {formatNumber(filteredContacts.length)}{' '}
              {filteredContacts.length === 1 ? 'person' : 'people'} opted in
              {purchaseFilter ? ` for ${purchaseFilter}` : ''}.
            </p>
            <div
              className="list-table four-col list-table--promo-emails"
              role="table"
            >
              <div className="flex-row list-table-header" role="rowgroup">
                <div
                  className="list-table-col list-table-col-header"
                  role="columnheader"
                >
                  <span>First name</span>
                </div>
                <div
                  className="list-table-col list-table-col-header"
                  role="columnheader"
                >
                  <span>Last name</span>
                </div>
                <div
                  className="list-table-col list-table-col-header"
                  role="columnheader"
                >
                  <span>Email</span>
                </div>
                <div
                  className="list-table-col list-table-col-header"
                  role="columnheader"
                >
                  <span>Purchase</span>
                </div>
              </div>
              {filteredContacts.map((contact) => (
                <div className="flex-row" role="rowgroup" key={contact.email}>
                  <div className="list-table-col text-truncate" role="cell">
                    <span>
                      {capitalizeString(contact.firstName || '') || '—'}
                    </span>
                  </div>
                  <div className="list-table-col text-truncate" role="cell">
                    <span>
                      {capitalizeString(contact.lastName || '') || '—'}
                    </span>
                  </div>
                  <div className="list-table-col text-truncate" role="cell">
                    <span>{contact.email}</span>
                  </div>
                  <div className="list-table-col text-truncate" role="cell">
                    <span>{purchaseFilter || contact.purchaseName || '—'}</span>
                  </div>
                  <span className="btn-more-col" role="cell">
                    <Button
                      variant="link"
                      className="promo-emails-remove-btn"
                      onClick={() => setContactToRemove(contact)}
                    >
                      Remove
                    </Button>
                  </span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <EmptyContainer>
            <p>
              {contacts.length > 0
                ? 'No one opted in for this purchase.'
                : 'No one has opted in to promo emails yet.'}
            </p>
          </EmptyContainer>
        )}
      </Card>

      <DeleteModal
        entity="contact"
        show={Boolean(contactToRemove)}
        isRemoving={isRemoving}
        handleClose={() => !isRemoving && setContactToRemove(null)}
        handleDelete={handleRemove}
        text={
          contactToRemove
            ? purchaseFilter
              ? `Remove ${removeName || contactToRemove.email} from ${purchaseFilter}? They can still remain opted in for other purchases.`
              : `Remove ${removeName || contactToRemove.email} (${contactToRemove.email}) from promo emails?`
            : ''
        }
      />
    </section>
  )
}
